<template>
  <div class="sensor-injections-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Sensor Injections</h1>
          <p>Gestión de inyecciones de comandos a sensores</p>
        </v-col>
      </v-row>
      
      <!-- Estadísticas rápidas -->
      <v-row>
        <v-col cols="12" md="3">
          <v-card color="primary" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-injection-syringe</v-icon>
                <div>
                  <div class="text-h4">{{ totalInjections }}</div>
                  <div class="text-caption">Total Inyecciones</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="success" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-check-circle</v-icon>
                <div>
                  <div class="text-h4">{{ activeCount }}</div>
                  <div class="text-caption">Activas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="info" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-shield-check</v-icon>
                <div>
                  <div class="text-h4">{{ reliableCount }}</div>
                  <div class="text-caption">Confiables</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="warning" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-message-reply</v-icon>
                <div>
                  <div class="text-h4">{{ ackRequiredCount }}</div>
                  <div class="text-caption">Requieren ACK</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Filtros -->
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedSensorSettingId"
            :items="sensorSettingOptions"
            label="Filtrar por Configuración"
            clearable
            @update:model-value="filterBySensorSetting"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedType"
            :items="typeOptions"
            label="Filtrar por Tipo"
            clearable
            @update:model-value="filterByType"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedCommandType"
            :items="commandTypeOptions"
            label="Filtrar por Comando"
            clearable
            @update:model-value="filterByCommandType"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filtrar por Estado"
            clearable
            @update:model-value="filterByStatus"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="sensorInjectionsList"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Inyecciones de Sensores</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Inyección
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.sensorSettingId="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.sensorSettingId }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip :color="getTypeColor(item.type)" small>
                <v-icon left size="small">{{ getTypeIcon(item.type) }}</v-icon>
                {{ item.type.toUpperCase() }}
              </v-chip>
            </template>
            <template v-slot:item.commandType="{ item }">
              <v-chip :color="getCommandTypeColor(item.commandType)" variant="outlined" small>
                <v-icon left size="small">{{ getCommandTypeIcon(item.commandType) }}</v-icon>
                {{ item.getCommandTypeDescription() }}
              </v-chip>
            </template>
            <template v-slot:item.retryPolicy="{ item }">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-chip v-bind="props" color="purple" variant="outlined" small>
                    <v-icon left size="small">mdi-restart</v-icon>
                    {{ item.retryPolicy.maxRetries }}x
                  </v-chip>
                </template>
                <span>{{ item.getRetryPolicyDescription() }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.requiresAck="{ item }">
              <v-chip :color="item.requiresAck ? 'success' : 'default'" small>
                <v-icon left size="small">
                  {{ item.requiresAck ? 'mdi-message-reply' : 'mdi-message-arrow-right' }}
                </v-icon>
                {{ item.requiresAck ? 'Sí' : 'No' }}
              </v-chip>
            </template>
            <template v-slot:item.reliability="{ item }">
              <v-chip :color="item.isReliable() ? 'success' : 'warning'" small>
                <v-icon left size="small">
                  {{ item.isReliable() ? 'mdi-shield-check' : 'mdi-shield-alert' }}
                </v-icon>
                {{ item.isReliable() ? 'Confiable' : 'Básica' }}
              </v-chip>
            </template>
            <template v-slot:item.active="{ item }">
              <v-switch
                v-model="item.active"
                :loading="toggleLoading[item.id]"
                color="success"
                @change="toggleStatus(item)"
                hide-details
                density="compact"
              ></v-switch>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn 
                size="small" 
                icon 
                variant="text" 
                @click="executeInjection(item)"
                :loading="executeLoading[item.id]"
                :disabled="!item.active"
                color="success"
              >
                <v-icon>mdi-play</v-icon>
                <v-tooltip activator="parent" location="top">
                  Ejecutar inyección
                </v-tooltip>
              </v-btn>
              <v-btn 
                size="small" 
                icon 
                variant="text" 
                @click="testInjection(item)"
                :loading="testLoading[item.id]"
                color="info"
              >
                <v-icon>mdi-test-tube</v-icon>
                <v-tooltip activator="parent" location="top">
                  Probar inyección
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">
                  Editar inyección
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">
                  Eliminar inyección
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from 'vue'
import { useSensorInjectionStore } from './store'
import { SensorInjection } from './models/sensorInjection'

const sensorInjectionStore = useSensorInjectionStore()
const loading = ref(false)
const selectedSensorSettingId = ref<string | null>(null)
const selectedType = ref<string | null>(null)
const selectedCommandType = ref<string | null>(null)
const statusFilter = ref<string | null>(null)
const executeLoading = reactive<Record<string, boolean>>({})
const testLoading = reactive<Record<string, boolean>>({})
const toggleLoading = reactive<Record<string, boolean>>({})

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Config ID', key: 'sensorSettingId' },
  { title: 'Tipo', key: 'type' },
  { title: 'Setting ID', key: 'settingId' },
  { title: 'Comando', key: 'commandType' },
  { title: 'Reintentos', key: 'retryPolicy' },
  { title: 'Req. ACK', key: 'requiresAck' },
  { title: 'Confiabilidad', key: 'reliability' },
  { title: 'Activo', key: 'active' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros
const sensorSettingOptions = ref([
  { title: 'Sensor Config 1', value: 'sensorConfig1' },
  { title: 'Sensor Config 2', value: 'sensorConfig2' },
  { title: 'Sensor Config 3', value: 'sensorConfig3' },
])

const typeOptions = ref([
  { title: 'MQTT', value: 'mqtt' },
  { title: 'HTTP', value: 'http' },
  { title: 'TCP', value: 'tcp' },
  { title: 'UDP', value: 'udp' },
  { title: 'Serial', value: 'serial' },
])

const commandTypeOptions = ref([
  { title: 'Establecer Valor', value: 'SET_VALUE' },
  { title: 'Obtener Estado', value: 'GET_STATUS' },
  { title: 'Calibrar', value: 'CALIBRATE' },
  { title: 'Reiniciar', value: 'RESET' },
  { title: 'Configurar', value: 'CONFIG' },
  { title: 'Iniciar', value: 'START' },
  { title: 'Detener', value: 'STOP' },
  { title: 'Personalizado', value: 'CUSTOM' },
])

const statusOptions = ref([
  { title: 'Activo', value: 'active' },
  { title: 'Inactivo', value: 'inactive' },
  { title: 'Confiable', value: 'reliable' },
  { title: 'Requiere ACK', value: 'requiresAck' },
])

const sensorInjectionsList = computed(() => {
  let list = sensorInjectionStore.sensorInjections
  
  if (selectedSensorSettingId.value) {
    list = list.filter(item => item.sensorSettingId === selectedSensorSettingId.value)
  }
  
  if (selectedType.value) {
    list = list.filter(item => item.type === selectedType.value)
  }
  
  if (selectedCommandType.value) {
    list = list.filter(item => item.commandType === selectedCommandType.value)
  }
  
  if (statusFilter.value) {
    switch (statusFilter.value) {
      case 'active':
        list = list.filter(item => item.active)
        break
      case 'inactive':
        list = list.filter(item => !item.active)
        break
      case 'reliable':
        list = list.filter(item => item.isReliable())
        break
      case 'requiresAck':
        list = list.filter(item => item.requiresAck)
        break
    }
  }
  
  return list
})

// Estadísticas computadas
const totalInjections = computed(() => sensorInjectionStore.sensorInjections.length)
const activeCount = computed(() => sensorInjectionStore.sensorInjections.filter(item => item.active).length)
const reliableCount = computed(() => sensorInjectionStore.sensorInjections.filter(item => item.isReliable()).length)
const ackRequiredCount = computed(() => sensorInjectionStore.sensorInjections.filter(item => item.requiresAck).length)

const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    mqtt: 'orange',
    http: 'green',
    tcp: 'blue',
    udp: 'purple',
    serial: 'red'
  }
  return colors[type] || 'grey'
}

const getTypeIcon = (type: string) => {
  const icons: Record<string, string> = {
    mqtt: 'mdi-message-processing',
    http: 'mdi-web',
    tcp: 'mdi-network',
    udp: 'mdi-network-outline',
    serial: 'mdi-cable-data'
  }
  return icons[type] || 'mdi-help-circle'
}

const getCommandTypeColor = (commandType: string) => {
  const colors: Record<string, string> = {
    'SET_VALUE': 'green',
    'GET_STATUS': 'blue',
    'CALIBRATE': 'orange',
    'RESET': 'red',
    'CONFIG': 'purple',
    'START': 'success',
    'STOP': 'error',
    'CUSTOM': 'grey'
  }
  return colors[commandType] || 'grey'
}

const getCommandTypeIcon = (commandType: string) => {
  const icons: Record<string, string> = {
    'SET_VALUE': 'mdi-cog',
    'GET_STATUS': 'mdi-information',
    'CALIBRATE': 'mdi-tune',
    'RESET': 'mdi-restart',
    'CONFIG': 'mdi-settings',
    'START': 'mdi-play',
    'STOP': 'mdi-stop',
    'CUSTOM': 'mdi-wrench'
  }
  return icons[commandType] || 'mdi-help-circle'
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorInjectionStore.getSensorInjections()
  } finally {
    loading.value = false
  }
}

const filterBySensorSetting = async (sensorSettingId: string | null) => {
  if (sensorSettingId) {
    loading.value = true
    try {
      await sensorInjectionStore.getSensorInjectionsBySensorSettingId(sensorSettingId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByType = async (type: string | null) => {
  if (type) {
    loading.value = true
    try {
      await sensorInjectionStore.getSensorInjectionsByType(type)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByCommandType = async (commandType: string | null) => {
  if (commandType) {
    loading.value = true
    try {
      await sensorInjectionStore.getSensorInjectionsByCommandType(commandType)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByStatus = () => {
  // El filtrado por estado se maneja en el computed
}

const addItem = () => {
  console.log('Add new sensor injection')
}

const editItem = (item: SensorInjection) => {
  console.log('Edit item:', item)
}

const deleteItem = (item: SensorInjection) => {
  console.log('Delete item:', item)
}

const executeInjection = async (item: SensorInjection) => {
  executeLoading[item.id] = true
  try {
    await sensorInjectionStore.executeSensorInjection(item.id)
    console.log(`Injection ${item.id} executed successfully`)
  } catch (error) {
    console.error('Error executing injection:', error)
  } finally {
    executeLoading[item.id] = false
  }
}

const testInjection = async (item: SensorInjection) => {
  testLoading[item.id] = true
  try {
    await sensorInjectionStore.testSensorInjection(item.id)
    console.log(`Injection ${item.id} tested successfully`)
  } catch (error) {
    console.error('Error testing injection:', error)
  } finally {
    testLoading[item.id] = false
  }
}

const toggleStatus = async (item: SensorInjection) => {
  toggleLoading[item.id] = true
  try {
    await sensorInjectionStore.toggleSensorInjectionStatus(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error toggling status:', error)
  } finally {
    toggleLoading[item.id] = false
  }
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.sensor-injections-view {
  height: 100%;
}
</style>
